import { Button } from '@/shared/Button';
import { MapOverlay } from '@/types/models';
import { twMerge } from 'tailwind-merge';
import Image from 'next/image';
import spinnerIcon from '@/assets/vectors/icons/spinner.svg';
import { ZERO } from '@/constants/common';
import { Icon } from '@/shared/Icon';
import React from 'react';
import { useOverlay } from '../../../hooks/useOverlay';
import { UserOverlayActions } from './UserOverlayActions';
import { UserOverlayInfo } from './UserOverlayInfo/UserOverlayInfo.component';
import { useDragAndDrop } from './hooks/useDragAndDrop';

type OverlayListItemProps = {
  index: number;
  moveUserOverlay: (dragIndex: number, hoverIndex: number) => void;
  userOverlay: MapOverlay;
  updateUserOverlaysOrder: (overlay: MapOverlay, targetGroupId: number | null) => void;
};

export const UserOverlayListItem = ({
  index,
  moveUserOverlay,
  userOverlay,
  updateUserOverlaysOrder,
}: OverlayListItemProps): JSX.Element => {
  const { toggleOverlay, isOverlayActive, isOverlayLoading } = useOverlay(userOverlay.id);
  const { dragRef, dropRef, isDragging } = useDragAndDrop({
    onDrop: updateUserOverlaysOrder,
    onHover: moveUserOverlay,
    index,
    groupId: userOverlay.group,
    overlay: userOverlay,
  });

  const { description } = userOverlay;

  return (
    <li
      ref={node => dragRef(dropRef(node))}
      className={twMerge(
        'flex flex-row flex-nowrap items-center justify-between overflow-visible py-4 pl-10 pr-5',
        isDragging ? 'opacity-0' : 'opacity-100',
      )}
    >
      {userOverlay.id > ZERO && <UserOverlayInfo name={userOverlay.name} />}

      {description !== '' && (
        <div className="ml-auto mr-1">
          <div className="group relative" data-testid="info">
            <Icon name="info" className="h-4 w-4 fill-black" />
            <div
              className={twMerge(
                'absolute bottom-0 right-5 top-auto z-20 hidden min-w-[200px] max-w-xs rounded-lg bg-white px-3 py-4 drop-shadow-md group-hover:block',
              )}
            >
              <p className="text-xs">{description}</p>
            </div>
          </div>
        </div>
      )}

      {userOverlay.id > ZERO && (
        <div className="flex flex-row flex-nowrap items-center">
          <Button
            variantStyles="ghost"
            className="mr-4 max-h-8 flex-none gap-1.5"
            onClick={toggleOverlay}
            data-testid="toggle-overlay-button"
          >
            {isOverlayLoading && (
              <Image
                src={spinnerIcon}
                alt="spinner icon"
                height={12}
                width={12}
                className="animate-spin"
              />
            )}
            {isOverlayActive || isOverlayLoading ? 'Hide' : 'View'}
          </Button>
          <UserOverlayActions overlay={userOverlay} />
        </div>
      )}
    </li>
  );
};
